import { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Mail, Shield, ShieldCheck, ShieldAlert, CheckCircle, XCircle, AlertTriangle, Search, Clock, ChevronDown, ChevronUp, Send, Copy, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const getBackendUrl = () => {
  if (window.location.hostname !== 'localhost') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

const BACKEND_URL = getBackendUrl();

function EmailSecurityPosture() {
  const [domain, setDomain] = useState('');
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [dkimSelectors, setDkimSelectors] = useState('');
  
  // Deep DKIM test state
  const [activeTab, setActiveTab] = useState('dns'); // 'dns' or 'deep'
  const [dkimTestSession, setDkimTestSession] = useState(null);
  const [startingTest, setStartingTest] = useState(false);
  const [checkingResult, setCheckingResult] = useState(false);
  const [dkimTestResult, setDkimTestResult] = useState(null);

  useEffect(() => {
    fetchHistory();
  }, []);

  const startDkimTest = async () => {
    setStartingTest(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/email-security/dkim-test/start`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setDkimTestSession(data);
        setDkimTestResult(null);
        toast.success('Test email address generated!');
      } else {
        throw new Error('Failed to start test');
      }
    } catch (error) {
      toast.error('Failed to generate test email');
    } finally {
      setStartingTest(false);
    }
  };

  const checkDkimTestResult = async () => {
    if (!dkimTestSession?.session_id) return;
    
    setCheckingResult(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/email-security/dkim-test/${dkimTestSession.session_id}/check`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'analyzed') {
          setDkimTestResult(data);
          toast.success('Email analyzed successfully!');
        } else if (data.status === 'pending') {
          toast.info('No email received yet. Please send an email to the test address.');
        } else if (data.status === 'expired') {
          toast.error('Test session expired. Please start a new test.');
          setDkimTestSession(null);
        }
      } else if (response.status === 410) {
        toast.error('Test session expired. Please start a new test.');
        setDkimTestSession(null);
      }
    } catch (error) {
      toast.error('Failed to check results');
    } finally {
      setCheckingResult(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const fetchHistory = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/email-security/history`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setHistory(data);
      }
    } catch (error) {
      console.error('Failed to load history');
    }
  };

  const handleCheck = async () => {
    if (!domain) {
      toast.error('Please enter a domain');
      return;
    }

    setChecking(true);
    toast.info('Checking email security records...');

    try {
      const selectors = dkimSelectors ? dkimSelectors.split(',').map(s => s.trim()).filter(s => s) : null;
      
      const response = await fetch(`${BACKEND_URL}/api/email-security/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          domain,
          dkim_selectors: selectors
        })
      });

      if (response.ok) {
        const data = await response.json();
        setResult(data);
        toast.success(`Check complete - Score: ${data.overall_score}/100 (${data.grade})`);
        fetchHistory();
      } else {
        throw new Error('Check failed');
      }
    } catch (error) {
      toast.error('Failed to check email security');
    } finally {
      setChecking(false);
    }
  };

  const getGradeColor = (grade) => {
    if (!grade) return 'text-muted-foreground';
    if (grade.startsWith('A')) return 'text-primary';
    if (grade === 'B') return 'text-accent';
    if (grade === 'C') return 'text-yellow-500';
    return 'text-destructive';
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-primary';
    if (score >= 60) return 'text-accent';
    if (score >= 40) return 'text-yellow-500';
    return 'text-destructive';
  };

  return (
    <DashboardLayout title="Email Security Posture">
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-accent/10 to-accent/5 border border-accent/20 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
              <Mail className="h-6 w-6 text-accent" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-mono font-bold mb-2">Email Security Posture Check</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Check your domain&apos;s email authentication records (SPF, DMARC, DKIM) to ensure 
                protection against email spoofing and phishing attacks.
              </p>
              <div className="flex flex-wrap gap-2 text-xs font-mono">
                <span className="px-3 py-1 bg-background rounded-full border border-border">SPF Check</span>
                <span className="px-3 py-1 bg-background rounded-full border border-border">DMARC Check</span>
                <span className="px-3 py-1 bg-background rounded-full border border-border">DKIM Check</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-border">
          <button
            onClick={() => setActiveTab('dns')}
            className={`px-4 py-2 font-mono text-sm border-b-2 transition-colors ${
              activeTab === 'dns' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            DNS Record Check
          </button>
          <button
            onClick={() => setActiveTab('deep')}
            className={`px-4 py-2 font-mono text-sm border-b-2 transition-colors ${
              activeTab === 'deep' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Deep DKIM Test
          </button>
        </div>

        {/* Deep DKIM Test Tab */}
        {activeTab === 'deep' && (
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-lg font-mono font-bold mb-4 flex items-center gap-2">
              <Send className="h-5 w-5 text-accent" />
              Deep DKIM Verification
            </h3>
            <p className="text-sm text-muted-foreground mb-6">
              Verify your email authentication by sending a test email. We&apos;ll receive it and analyze
              the DKIM signature and SPF authentication to confirm your email server is properly configured.
            </p>

            {!dkimTestSession ? (
              <div className="text-center py-8">
                <Mail className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">
                  Generate a unique test email address, then send an email from your domain to verify DKIM signing.
                </p>
                <Button
                  onClick={startDkimTest}
                  disabled={startingTest}
                  className="bg-accent text-accent-foreground hover:bg-accent/90"
                >
                  {startingTest ? (
                    <>
                      <Clock className="h-4 w-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4 mr-2" />
                      Generate Test Email Address
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Step 1: Test Email Address */}
                <div className="p-4 bg-background rounded-lg border-2 border-accent/30">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="h-6 w-6 rounded-full bg-accent text-accent-foreground flex items-center justify-center text-xs font-bold">1</div>
                    <h4 className="font-mono font-bold">Send an email to this address:</h4>
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <code className="flex-1 px-4 py-3 bg-card rounded border border-border font-mono text-primary break-all">
                      {dkimTestSession.test_email}
                    </code>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(dkimTestSession.test_email)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Send from your company email (e.g., you@yourdomain.com). Subject and content don&apos;t matter.
                  </p>
                </div>

                {/* Step 2: Check Results */}
                <div className="p-4 bg-background rounded-lg border border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="h-6 w-6 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-xs font-bold">2</div>
                    <h4 className="font-mono font-bold">Check the results:</h4>
                  </div>
                  <div className="flex items-center gap-3 mt-3">
                    <Button
                      onClick={checkDkimTestResult}
                      disabled={checkingResult}
                      className="bg-primary text-primary-foreground"
                    >
                      {checkingResult ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Checking...
                        </>
                      ) : (
                        <>
                          <Search className="h-4 w-4 mr-2" />
                          Check Results
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setDkimTestSession(null);
                        setDkimTestResult(null);
                      }}
                    >
                      Start New Test
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Session expires in {dkimTestSession.expires_in_minutes} minutes
                  </p>
                </div>

                {/* Results */}
                {dkimTestResult && dkimTestResult.status === 'analyzed' && (
                  <div className="space-y-4">
                    <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
                      <h4 className="font-mono font-bold mb-3 flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-primary" />
                        Email Received & Analyzed
                      </h4>
                      {dkimTestResult.sender_info && (
                        <div className="text-sm mb-4">
                          <p><span className="text-muted-foreground">From:</span> {dkimTestResult.sender_info.from}</p>
                          <p><span className="text-muted-foreground">Subject:</span> {dkimTestResult.sender_info.subject}</p>
                        </div>
                      )}
                    </div>

                    {/* DKIM Result */}
                    <div className="p-4 bg-background rounded-lg border border-border">
                      <div className="flex items-center gap-3 mb-3">
                        {dkimTestResult.dkim_result?.valid ? (
                          <CheckCircle className="h-6 w-6 text-primary" />
                        ) : dkimTestResult.dkim_result?.signature_found ? (
                          <AlertTriangle className="h-6 w-6 text-yellow-500" />
                        ) : (
                          <XCircle className="h-6 w-6 text-destructive" />
                        )}
                        <h4 className="font-mono font-bold">DKIM Signature</h4>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-mono ${
                          dkimTestResult.dkim_result?.valid 
                            ? 'bg-primary/10 text-primary' 
                            : dkimTestResult.dkim_result?.signature_found
                            ? 'bg-yellow-500/10 text-yellow-500'
                            : 'bg-destructive/10 text-destructive'
                        }`}>
                          {dkimTestResult.dkim_result?.valid ? 'VALID' : dkimTestResult.dkim_result?.signature_found ? 'FOUND' : 'NOT FOUND'}
                        </span>
                      </div>
                      {dkimTestResult.dkim_result?.domain && (
                        <p className="text-sm"><span className="text-muted-foreground">Domain:</span> {dkimTestResult.dkim_result.domain}</p>
                      )}
                      {dkimTestResult.dkim_result?.selector && (
                        <p className="text-sm"><span className="text-muted-foreground">Selector:</span> {dkimTestResult.dkim_result.selector}</p>
                      )}
                      {dkimTestResult.dkim_result?.algorithm && (
                        <p className="text-sm"><span className="text-muted-foreground">Algorithm:</span> {dkimTestResult.dkim_result.algorithm}</p>
                      )}
                      {dkimTestResult.dkim_result?.details?.map((detail, idx) => (
                        <p key={idx} className="text-sm text-primary mt-2">{detail}</p>
                      ))}
                      {dkimTestResult.dkim_result?.issues?.map((issue, idx) => (
                        <p key={idx} className="text-sm text-destructive mt-2">{issue}</p>
                      ))}
                    </div>

                    {/* SPF Result */}
                    <div className="p-4 bg-background rounded-lg border border-border">
                      <div className="flex items-center gap-3 mb-3">
                        {dkimTestResult.spf_result?.valid ? (
                          <CheckCircle className="h-6 w-6 text-primary" />
                        ) : (
                          <XCircle className="h-6 w-6 text-destructive" />
                        )}
                        <h4 className="font-mono font-bold">SPF Check</h4>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-mono ${
                          dkimTestResult.spf_result?.valid 
                            ? 'bg-primary/10 text-primary' 
                            : 'bg-destructive/10 text-destructive'
                        }`}>
                          {dkimTestResult.spf_result?.result?.toUpperCase() || 'UNKNOWN'}
                        </span>
                      </div>
                      {dkimTestResult.spf_result?.details?.map((detail, idx) => (
                        <p key={idx} className="text-sm text-primary">{detail}</p>
                      ))}
                      {dkimTestResult.spf_result?.issues?.map((issue, idx) => (
                        <p key={idx} className="text-sm text-destructive">{issue}</p>
                      ))}
                    </div>

                    {/* Raw Message Headers */}
                    {dkimTestResult.raw_headers && (
                      <div className="p-4 bg-background rounded-lg border border-border">
                        <button
                          onClick={() => setShowAdvanced(!showAdvanced)}
                          className="flex items-center gap-2 font-mono font-bold w-full text-left"
                        >
                          {showAdvanced ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          <Mail className="h-5 w-5 text-muted-foreground" />
                          Message Headers
                        </button>
                        {showAdvanced && (
                          <div className="mt-4">
                            <div className="relative">
                              <Button
                                variant="outline"
                                size="sm"
                                className="absolute top-2 right-2 z-10"
                                onClick={() => copyToClipboard(dkimTestResult.raw_headers)}
                              >
                                <Copy className="h-3 w-3 mr-1" />
                                Copy
                              </Button>
                              <pre className="p-4 bg-card rounded border border-border font-mono text-xs overflow-x-auto max-h-96 whitespace-pre-wrap break-all">
                                {dkimTestResult.raw_headers}
                              </pre>
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">
                              These are the raw email headers received by our test server. Look for DKIM-Signature and Authentication-Results headers.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* DNS Check Form (existing) */}
        {activeTab === 'dns' && (
          <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-mono font-bold mb-4 flex items-center gap-2">
            <Search className="h-5 w-5 text-primary" />
            Check Your Domain
          </h3>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="domain" className="font-mono">Domain Name</Label>
              <div className="flex gap-3 mt-1">
                <Input
                  id="domain"
                  placeholder="example.com"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  className="flex-1 font-mono"
                  onKeyDown={(e) => e.key === 'Enter' && !checking && handleCheck()}
                />
                <Button
                  onClick={handleCheck}
                  disabled={checking || !domain}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 font-mono"
                >
                  {checking ? (
                    <>
                      <Clock className="h-4 w-4 mr-2 animate-spin" />
                      Checking...
                    </>
                  ) : (
                    <>
                      <ShieldCheck className="h-4 w-4 mr-2" />
                      Check Now
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Advanced Options */}
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
            >
              {showAdvanced ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              Advanced Options
            </button>

            {showAdvanced && (
              <div className="p-4 bg-background rounded-md border border-border">
                <Label htmlFor="dkim-selectors" className="font-mono text-sm">Custom DKIM Selectors (comma-separated)</Label>
                <Input
                  id="dkim-selectors"
                  placeholder="selector1, selector2, google"
                  value={dkimSelectors}
                  onChange={(e) => setDkimSelectors(e.target.value)}
                  className="mt-1 font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Leave blank to check common selectors (google, selector1, default, etc.)
                </p>
              </div>
            )}
          </div>
        </div>
        )}

        {/* Results */}
        {result && activeTab === 'dns' && (
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-mono font-bold">Results for {result.domain}</h3>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">Score</p>
                  <p className={`text-3xl font-mono font-bold ${getScoreColor(result.overall_score)}`}>
                    {result.overall_score}
                  </p>
                </div>
                <div className={`h-16 w-16 rounded-full border-4 flex items-center justify-center ${
                  result.overall_score >= 80 ? 'border-primary bg-primary/10' :
                  result.overall_score >= 60 ? 'border-accent bg-accent/10' :
                  result.overall_score >= 40 ? 'border-yellow-500 bg-yellow-500/10' :
                  'border-destructive bg-destructive/10'
                }`}>
                  <span className={`text-2xl font-mono font-bold ${getGradeColor(result.grade)}`}>
                    {result.grade}
                  </span>
                </div>
              </div>
            </div>

            {/* SPF Results */}
            <div className="mb-6 p-4 bg-background rounded-lg border border-border">
              <div className="flex items-center gap-3 mb-3">
                {result.spf_valid ? (
                  <CheckCircle className="h-6 w-6 text-primary" />
                ) : (
                  <XCircle className="h-6 w-6 text-destructive" />
                )}
                <h4 className="font-mono font-bold">SPF (Sender Policy Framework)</h4>
                <span className={`px-2 py-0.5 rounded-full text-xs font-mono ${
                  result.spf_valid ? 'bg-primary/10 text-primary' : 'bg-destructive/10 text-destructive'
                }`}>
                  {result.spf_valid ? 'PASS' : 'FAIL'}
                </span>
              </div>
              {result.spf_record && (
                <div className="bg-card p-3 rounded border border-border mb-2">
                  <code className="text-xs font-mono break-all">{result.spf_record}</code>
                </div>
              )}
              {result.spf_issues?.map((issue, idx) => (
                <div key={idx} className="flex items-start gap-2 text-sm text-muted-foreground mt-2">
                  <AlertTriangle className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>{issue}</span>
                </div>
              ))}
            </div>

            {/* DMARC Results */}
            <div className="mb-6 p-4 bg-background rounded-lg border border-border">
              <div className="flex items-center gap-3 mb-3">
                {result.dmarc_valid ? (
                  <CheckCircle className="h-6 w-6 text-primary" />
                ) : (
                  <XCircle className="h-6 w-6 text-destructive" />
                )}
                <h4 className="font-mono font-bold">DMARC (Domain-based Message Authentication)</h4>
                <span className={`px-2 py-0.5 rounded-full text-xs font-mono ${
                  result.dmarc_valid ? 'bg-primary/10 text-primary' : 'bg-destructive/10 text-destructive'
                }`}>
                  {result.dmarc_valid ? 'PASS' : 'FAIL'}
                </span>
                {result.dmarc_policy && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-mono ${
                    result.dmarc_policy === 'reject' ? 'bg-primary/20 text-primary' :
                    result.dmarc_policy === 'quarantine' ? 'bg-accent/20 text-accent' :
                    'bg-yellow-500/20 text-yellow-500'
                  }`}>
                    Policy: {result.dmarc_policy}
                  </span>
                )}
              </div>
              {result.dmarc_record && (
                <div className="bg-card p-3 rounded border border-border mb-2">
                  <code className="text-xs font-mono break-all">{result.dmarc_record}</code>
                </div>
              )}
              {result.dmarc_issues?.map((issue, idx) => (
                <div key={idx} className="flex items-start gap-2 text-sm text-muted-foreground mt-2">
                  <AlertTriangle className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>{issue}</span>
                </div>
              ))}
            </div>

            {/* DKIM Results */}
            <div className="mb-6 p-4 bg-background rounded-lg border border-border">
              <div className="flex items-center gap-3 mb-3">
                {result.dkim_valid ? (
                  <CheckCircle className="h-6 w-6 text-primary" />
                ) : (
                  <XCircle className="h-6 w-6 text-destructive" />
                )}
                <h4 className="font-mono font-bold">DKIM (DomainKeys Identified Mail)</h4>
                <span className={`px-2 py-0.5 rounded-full text-xs font-mono ${
                  result.dkim_valid ? 'bg-primary/10 text-primary' : 'bg-destructive/10 text-destructive'
                }`}>
                  {result.dkim_valid ? 'PASS' : 'FAIL'}
                </span>
              </div>
              {result.dkim_records?.length > 0 ? (
                <div className="space-y-2">
                  {result.dkim_records.map((dkim, idx) => (
                    <div key={idx} className="bg-card p-3 rounded border border-border">
                      <p className="text-xs font-mono text-muted-foreground mb-1">
                        Selector: <span className="text-primary">{dkim.selector}</span>
                      </p>
                      <code className="text-xs font-mono break-all">{dkim.record}</code>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No DKIM records found for common selectors</p>
              )}
              {result.dkim_issues?.map((issue, idx) => (
                <div key={idx} className="flex items-start gap-2 text-sm text-muted-foreground mt-2">
                  <AlertTriangle className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>{issue}</span>
                </div>
              ))}
            </div>

            {/* Recommendations */}
            {result.recommendations?.length > 0 && (
              <div className="p-4 bg-accent/5 border border-accent/20 rounded-lg">
                <h4 className="font-mono font-bold mb-3 flex items-center gap-2">
                  <Shield className="h-5 w-5 text-accent" />
                  Recommendations
                </h4>
                <div className="space-y-2">
                  {result.recommendations.map((rec, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-sm font-mono">{rec}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* History */}
        {history.length > 0 && activeTab === 'dns' && (
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-lg font-mono font-bold mb-4">Recent Checks</h3>
            <div className="space-y-2">
              {history.slice(0, 5).map((check, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-3 bg-background rounded-md border border-border hover:border-primary/50 cursor-pointer"
                  onClick={() => setResult(check)}
                >
                  <div className="flex items-center gap-3">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="font-mono">{check.domain}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`text-sm font-mono font-bold ${getScoreColor(check.overall_score)}`}>
                      {check.overall_score}/100
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-mono font-bold ${
                      check.grade?.startsWith('A') ? 'bg-primary/20 text-primary' :
                      check.grade === 'B' ? 'bg-accent/20 text-accent' :
                      check.grade === 'C' ? 'bg-yellow-500/20 text-yellow-500' :
                      'bg-destructive/20 text-destructive'
                    }`}>
                      {check.grade}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(check.checked_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default EmailSecurityPosture;
